namespace $ {
	export type $shm_projects_goal_timings = {
		workspace_ms: number
		builder_ms: number
		clarification_ms: number
		waiting_ms: number
		implementation_ms: number
		visual_review_ms: number
		checkpoint_ms: number
		prerelease_ms: number
		deployment_ms: number
	}

	export type $shm_projects_goal_run = {
		id: string
		project_id: string
		project?: $shm_projects_data_record
		goal: string
		refinements: readonly string[]
		status: 'queued' | 'running' | 'waiting' | 'done' | 'failed' | 'stopped'
		phase: string
		started_at: number
		iteration_started_at?: number
		updated_at: number
		completed_at: number | null
		elapsed_ms: number
		timings?: $shm_projects_goal_timings
		iteration: number
		session_id: string
		summary: string
		error: string
		recovery_attempts?: number
		deployment_recovery_attempts?: number
		deployment_error?: string
		worker_pid?: number
		workspace?: {
			id: string
			active: boolean
		}
		operation?: 'clarification' | 'goal' | 'prerelease'
		clarifications?: readonly {
			id: string
			question: string
			answer: string
		}[]
		events: readonly {
			time: number
			phase: string
			text: string
			kind?: 'message' | 'reasoning' | 'system'
			iteration?: number
			scope?: 'iteration' | 'prerelease'
		}[]
		prerelease?: {
			status: 'queued' | 'running' | 'done' | 'failed' | 'stopped'
			started_at: number
			completed_at: number | null
			summary: string
			error: string
			events: readonly {
				time: number
				phase: string
				text: string
				kind?: 'message' | 'reasoning' | 'system'
				iteration?: number
				scope?: 'iteration' | 'prerelease'
			}[]
			results: readonly {
				title: string
				description: string
				screenshots: readonly string[]
			}[]
			artifacts: readonly {
				type: 'screenshot' | 'video' | 'report'
				path: string
			}[]
			external_error?: string
			external_error_accepted_at?: number
		}
		results?: readonly {
			title: string
			description: string
			screenshots: readonly string[]
		}[]
		result_history?: readonly {
			iteration: number
			summary: string
			clarifications?: readonly {
				id: string
				question: string
				answer: string
			}[]
			started_at?: number
			completed_at: number
			elapsed_ms?: number
			timings?: $shm_projects_goal_timings
			artifacts?: readonly {
				type: 'screenshot' | 'video' | 'report'
				path: string
			}[]
			changes: readonly {
				title: string
				description: string
				screenshots: readonly string[]
			}[]
		}[]
			repositories?: readonly {
				path: string
				branch: string
				base_branch: string
				base_commit: string
				initial_status?: readonly string[]
			}[]
		checkpoints?: readonly {
			iteration: number
			commits: Record< string, string >
		}[]
		artifacts: readonly {
			type: 'screenshot' | 'video' | 'report'
			path: string
		}[]
		deployments: readonly {
			stage: 'Пререлиз' | 'Релиз'
			status: 'planned' | 'deployed' | 'failed'
			driver: 'docker'
			target: string
			server: string
			context: string
			image: string
			execute: boolean
			created_at: number
			from_version?: string
			channel_project_id?: string
			version: string
			delivery_project_id?: string
			from_delivery_version?: string
			delivery_version?: string
			date: string
			changes: string
			record: $shm_projects_data_version
			steps: readonly string[]
			artifact?: {
				id: string
				sha256: string
				manifest: string
				bytes: number
				files: number
			}
			components?: readonly $shm_projects_release_component_artifact[]
			deployed_at?: number
			finalized_at?: number
			error?: string
		}[]
	}

	export type $shm_projects_goal_project_create = {
		project: $shm_projects_data_record
		projects: readonly $shm_projects_data_record[]
	}

	export type $shm_projects_task_record = {
		id: string
		project_id: string
		title: string
		description: string
		result: string
		run_id: string
		status: $shm_projects_goal_run['status'] | 'draft'
		created_at: number
		updated_at: number
	}

	export class $shm_projects_goal_api extends $mol_object2 {

		static base() {
			return this.$.$shm_projects_config.projects_api_url()
		}

		static async json( body?: object, id = '', project_id = '' ) {
			const url = new URL( this.base() )
			if( id ) url.searchParams.set( 'id', id )
			if( project_id ) url.searchParams.set( 'project_id', project_id )
			const token = this.$.$shm_projects_config.agent_token()
			const headers: Record< string, string > = {}
			if( body ) headers[ 'Content-Type' ] = 'application/json'
			if( token ) headers[ 'X-Hitalama-Agent-Token' ] = token
			const response = await fetch( url, body ? {
				method: 'POST',
				headers,
				body: JSON.stringify( body ),
			} : { cache: 'no-store', headers } )
			const data = await response.json()
			if( !response.ok || ( data?.error && !data?.id ) ) throw new Error( data?.error || `${ response.status } ${ response.statusText }` )
			return data
		}

		static start( project_id: string, goal: string, ask_questions = false, attachments: readonly $shm_projects_goal_attachment[] = [] ) {
			return this.json({ action: 'start', project_id, goal, ask_questions, attachments }) as Promise< $shm_projects_goal_run >
		}

		static project_create( title: string ) {
			return this.json({ action: 'project_create', title }) as Promise< $shm_projects_goal_project_create >
		}

		static project_archive( project_id: string ) {
			return this.json({ action: 'project_archive', project_id }) as Promise< $shm_projects_goal_project_create >
		}

		static project_delete( project_id: string ) {
			return this.json({ action: 'project_delete', project_id }) as Promise< $shm_projects_goal_project_create >
		}

		static project_rename( project_id: string, title: string ) {
			return this.json({ action: 'project_rename', project_id, title }) as Promise< $shm_projects_goal_project_create >
		}

		static project_product_update( project_id: string, product: $shm_projects_data_product ) {
			return this.json({ action: 'project_product_update', project_id, product }) as Promise< $shm_projects_goal_project_create >
		}

		static task_start( project_id: string, task_id: string, description: string ) {
			return this.json({ action: 'task_start', project_id, task_id, description }) as Promise< $shm_projects_goal_run >
		}

		static task_message( id: string, message: string ) {
			return this.json({ action: 'task_message', id, message }) as Promise< $shm_projects_goal_run >
		}

		static refine( id: string, goal: string, ask_questions = false, attachments: readonly $shm_projects_goal_attachment[] = [] ) {
			return this.json({ action: 'refine', id, goal, ask_questions, attachments }) as Promise< $shm_projects_goal_run >
		}

		static clarify( id: string, answers: readonly { id: string, answer: string }[] ) {
			return this.json({ action: 'clarify', id, answers }) as Promise< $shm_projects_goal_run >
		}

		static stop( id: string ) {
			return this.json({ action: 'stop', id }) as Promise< $shm_projects_goal_run >
		}

		static continue( id: string ) {
			return this.json({ action: 'continue', id }) as Promise< $shm_projects_goal_run >
		}

		static rollback( id: string, iteration: number ) {
			return this.json({ action: 'rollback', id, iteration }) as Promise< $shm_projects_goal_run >
		}

		static run( id: string ) {
			return this.json( undefined, id ) as Promise< $shm_projects_goal_run >
		}

		static active( project_id: string ) {
			return this.json( undefined, '', project_id ) as Promise< $shm_projects_goal_run | null >
		}

		static deploy( id: string, action: 'prerelease' | 'release', changes: string ) {
			return this.json({ action, id, changes }) as Promise< $shm_projects_goal_run >
		}

		static prerelease_retry( id: string ) {
			return this.json({ action: 'prerelease_retry', id }) as Promise< $shm_projects_goal_run >
		}

		static prerelease_accept_external( id: string ) {
			return this.json({ action: 'prerelease_accept_external', id }) as Promise< $shm_projects_goal_run >
		}

		static version_activate( project_id: string, version: string, stage: 'prerelease' | 'release' ) {
			return this.json({ action: 'version_activate', project_id, version, stage }) as Promise< $shm_projects_goal_run >
		}

	}

	export class $shm_projects_goal_api_events extends $mol_object {

		private socket: WebSocket | null = null
		private reconnect_timer: ReturnType< typeof setTimeout > | null = null
		private destroyed = false

		id() { return '' }
		notify() { return ( run: $shm_projects_goal_run )=> {} }

		native() {
			if( this.socket ) return this.socket
			const url = new URL( $shm_projects_goal_api.base() )
			url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
			const token = this.$.$shm_projects_config.agent_token()
			if( token ) url.searchParams.set( 'token', token )
			const socket = this.socket = new this.$.$mol_dom_context.WebSocket( url, 'shm-projects-goal' )
			socket.onopen = ()=> socket.send( JSON.stringify({ action: 'subscribe', id: this.id() }) )
			socket.onmessage = event => $mol_wire_async( this ).message( event )
			socket.onerror = ()=> {}
			socket.onclose = ()=> $mol_wire_async( this ).closed()
			return socket
		}

		message( event: MessageEvent ) {
			if( typeof event.data !== 'string' ) return
			try {
				this.notify()( JSON.parse( event.data ) as $shm_projects_goal_run )
			} catch( error ) {
				$mol_fail_log( error )
			}
		}

		closed() {
			this.socket = null
			if( this.destroyed || this.reconnect_timer ) return
			this.reconnect_timer = setTimeout( ()=> {
				this.reconnect_timer = null
				if( !this.destroyed ) this.native()
			}, 1000 )
		}

		override destructor() {
			this.destroyed = true
			if( this.reconnect_timer ) clearTimeout( this.reconnect_timer )
			this.reconnect_timer = null
			const socket = this.socket
			this.socket = null
			if( socket ) {
				socket.onopen = null
				socket.onmessage = null
				socket.onerror = null
				socket.onclose = null
				socket.close()
			}
			super.destructor()
		}

	}

}
